import { SKIP, visit } from "unist-util-visit";
import type { Literal } from "mdast";
import type { Options as ToMarkdownExtension } from "remark-stringify";
import { formatTemplateToken } from "@/components/SpotflowV2/Sidebar/variableRefs";

export const TEMPLATE_TOKEN_PATTERN = /\{\{([^{}\n]+)\}\}/g;

export interface TemplateTokenMdastNode extends Literal {
  type: "templateToken";
}

declare module "mdast" {
  interface PhrasingContentMap {
    templateToken: TemplateTokenMdastNode;
  }
  interface RootContentMap {
    templateToken: TemplateTokenMdastNode;
  }
}

// Serializes a `templateToken` node back to its literal `{{name}}` text as a
// raw handler, NOT a text node: remark-stringify escapes markdown punctuation
// in text (`loop_item` → `loop\_item`), and the playbook executor matches the
// name verbatim, so the token must bypass escaping entirely.
export const templateTokenToMarkdown: ToMarkdownExtension = {
  handlers: {
    templateToken: (node: TemplateTokenMdastNode) =>
      formatTemplateToken(node.value),
  },
};

interface UnistNode {
  type: string;
  value?: string;
  children?: UnistNode[];
}

function isParent(
  node: UnistNode | undefined | null
): node is UnistNode & { children: UnistNode[] } {
  return node != null && Array.isArray(node.children);
}

function splitTemplateTokensInText(value: string): UnistNode[] | null {
  const matches = [
    ...value.matchAll(new RegExp(TEMPLATE_TOKEN_PATTERN.source, "g")),
  ];
  if (matches.length === 0) return null;
  const parts: UnistNode[] = [];
  let last = 0;
  for (const match of matches) {
    const start = match.index ?? 0;
    if (start > last) {
      parts.push({ type: "text", value: value.slice(last, start) });
    }
    parts.push({ type: "templateToken", value: match[1] ?? "" });
    last = start + match[0].length;
  }
  if (last < value.length) {
    parts.push({ type: "text", value: value.slice(last) });
  }
  return parts;
}

// Splits `{{name}}` playbook template tokens out of text nodes into
// `templateToken` mdast nodes so the editor can promote them to chip nodes.
// Code spans and fences are leaf nodes carrying a raw `value`, so the walk
// never reaches inside one and cannot chip-ify their contents.
export function splitTemplateTokens(tree: unknown) {
  visit(tree as Parameters<typeof visit>[0], "text", (node, index, parent) => {
    const p = parent as UnistNode | undefined;
    if (!isParent(p) || index == null) return;
    const value = (node as UnistNode).value;
    if (!value) return;
    const parts = splitTemplateTokensInText(value);
    if (!parts) return;
    p.children.splice(index, 1, ...parts);
    return [SKIP, index + parts.length];
  });
}
